import React, { useState, useEffect, useMemo, useRef } from "react";
import { LineChart, Line } from "recharts";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from "react-router-dom"; // if using React Router

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const dashboardStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  
  :root {
    --navy-900: #0B1F40;
    --navy-800: #0f2a55;
    --navy-700: #132f60;
    --navy-600: #163569;
    --navy-500: #1a3d7a;
    --navy-400: #1e4589;
    --accent-gold: #F4B942;
    --accent-gold-light: #f9d07a;
    --accent-cyan: #38BDF8;
    --accent-cyan-light: #0ea5e9;
    --accent-emerald: #10B981;
    --accent-rose: #F43F5E;
    --accent-violet: #8B5CF6;
    --glass-bg: rgba(15, 42, 85, 0.04);
    --glass-border: rgba(15, 42, 85, 0.12);
    --glass-hover: rgba(15, 42, 85, 0.08);
    --text-primary: #0B1F40;
    --text-secondary: #1a3d7a;
    --text-muted: #5a7ba8;
    --shadow-glow: 0 0 40px rgba(56,189,248,0.15);
    --shadow-card: 0 4px 24px rgba(11, 31, 64, 0.08);
    --radius-lg: 20px;
    --radius-md: 14px;
    --radius-sm: 10px;
  }

  .dash-root {
    font-family: 'Sora', sans-serif;
    background: #fff;
    min-height: 100vh;
    padding: 28px 32px 60px;
    position: relative;
    overflow-x: hidden; 
  }

  .dash-root::before {
    content: '';
    position: fixed;
    top: -200px;
    right: -200px;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .dash-root::after {
    content: '';
    position: fixed;
    bottom: -200px;
    left: -100px;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(244,185,66,0.08) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .dash-content {
    position: relative;
    z-index: 1;
  }

  /* Header */
  .dash-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 36px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--glass-border);
  }

  .dash-title-wrap {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .dash-title-icon {
    width: 46px;
    height: 46px;
    background: linear-gradient(135deg, var(--accent-cyan), #0ea5e9);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #fff;
    box-shadow: 0 4px 20px rgba(56,189,248,0.35);
  }

  .dash-title-text {
    font-size: 26px;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.5px;
    margin: 0;
  }

  .dash-title-sub {
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 400;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .dash-header-controls {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  /* Selects */
  .dash-select-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .dash-select-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .dash-select {
    background: #fff;
    border: 1.5px solid var(--glass-border);
    color: var(--text-primary);
    font-family: 'Sora', sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 9px 34px 9px 14px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    outline: none;
    transition: all 0.25s ease;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2338BDF8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    min-width: 180px;
    box-shadow: 0 1px 3px rgba(11, 31, 64, 0.05);
  }

  .dash-select:hover, .dash-select:focus {
    border-color: var(--accent-cyan);
    box-shadow: 0 0 0 3px rgba(56,189,248,0.15);
    background-color: rgba(56,189,248,0.04);
  }

  .dash-select option {
    background: #fff;
    color: var(--text-primary);
  }

  /* Stat Cards Row */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-bottom: 28px;
  }

  .stat-card {
    background: #fff;
    position: relative;
    border-radius: var(--radius-lg);
    padding: 24px;
    overflow: hidden;
    border: 1.5px solid var(--glass-border);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: default;
  }

  .stat-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-glow);
  }

  .stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }

  .stat-card-login {
    background: linear-gradient(145deg, rgba(244,185,66,0.06), rgba(244,185,66,0.02));
    box-shadow: 0 4px 16px rgba(244,185,66,0.1);
  }
  .stat-card-login::before { background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light)); }

  .stat-card-admissions {
    background: linear-gradient(145deg, rgba(56,189,248,0.06), rgba(56,189,248,0.02));
    box-shadow: 0 4px 16px rgba(56,189,248,0.1);
  }
  .stat-card-admissions::before { background: linear-gradient(90deg, var(--accent-cyan), var(--accent-cyan-light)); }

  .stat-card-followup {
    background: linear-gradient(145deg, rgba(16,185,129,0.06), rgba(16,185,129,0.02));
    box-shadow: 0 4px 16px rgba(16,185,129,0.1);
  }
  .stat-card-followup::before { background: linear-gradient(90deg, var(--accent-emerald), #34d399); }

  .stat-card-due {
    background: linear-gradient(145deg, rgba(244,63,94,0.06), rgba(244,63,94,0.02));
    box-shadow: 0 4px 16px rgba(244,63,94,0.1);
  }
  .stat-card-due::before { background: linear-gradient(90deg, var(--accent-rose), #fb7185); }

  .stat-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 18px;
  }

  .stat-card-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }

  .stat-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    opacity: 1;
  }

  .icon-gold { background: rgba(244,185,66,0.15); color: #d89600; }
  .icon-cyan { background: rgba(56,189,248,0.15); color: #0284c7; }
  .icon-emerald { background: rgba(16,185,129,0.15); color: #059669; }
  .icon-rose { background: rgba(244,63,94,0.15); color: #e11d48; }

  .stat-total-value {
    font-size: 32px;
    font-weight: 800;
    color: var(--text-primary);
    letter-spacing: -1px;
    line-height: 1;
    margin-bottom: 16px;
    font-family: 'JetBrains Mono', monospace;
  }

  .stat-total-label {
    font-size: 11px;
    color: var(--text-muted);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: block;
    margin-bottom: 4px;
  }

  .stat-grid-bottom {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--glass-border);
  }

  .stat-mini {
    text-align: center;
  }

  .stat-mini-label {
    font-size: 10px;
    color: var(--text-muted);
    font-weight: 500;
    display: block;
    margin-bottom: 3px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .stat-mini-value {
    font-size: 15px;
    font-weight: 700;
    color: var(--text-primary);
    font-family: 'JetBrains Mono', monospace;
  }

  /* Section Cards */
  .section-card {
    background: #fff;
    border: 1.5px solid var(--glass-border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-card);
    margin-bottom: 24px;
  }

  .section-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 22px 28px 18px;
    border-bottom: 1.5px solid var(--glass-border);
    background: linear-gradient(90deg, rgba(56,189,248,0.03), transparent);
  }

  .section-title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .section-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    background: linear-gradient(135deg, rgba(56,189,248,0.15), rgba(56,189,248,0.08));
    color: #0284c7;
    border: 1px solid rgba(56,189,248,0.2);
  }

  .section-title {
    font-size: 17px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
    letter-spacing: -0.3px;
  }

  .section-body {
    padding: 24px 28px;
  }

  /* Frequency Radio Controls */
  .freq-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(15, 42, 85, 0.04);
    padding: 5px;
    border-radius: 10px;
    border: 1.5px solid var(--glass-border);
  }

  .freq-label-text {
    font-size: 12px;
    color: var(--text-muted);
    padding: 0 8px 0 6px;
    font-weight: 500;
  }

  .freq-btn {
    position: relative;
    display: inline-block;
  }

  .freq-btn input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .freq-btn label {
    display: block;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    border-radius: 7px;
    transition: all 0.2s ease;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }

  .freq-btn input[type="radio"]:checked + label {
    background: linear-gradient(135deg, var(--accent-cyan), #0ea5e9);
    color: #fff;
    box-shadow: 0 2px 12px rgba(56,189,248,0.4);
  }

  .freq-btn label:hover {
    color: var(--text-primary);
    background: rgba(15, 42, 85, 0.08);
  }

  /* Class Select in attendance section */
  .attend-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 24px;
  }

  /* Chart Styling */
  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: rgba(15, 42, 85, 0.08) !important;
  }

  .recharts-text {
    fill: var(--text-muted) !important;
    font-size: 11px !important;
    font-family: 'Sora', sans-serif !important;
  }

  .recharts-legend-item-text {
    color: var(--text-secondary) !important;
    font-size: 12px !important;
  }

  .recharts-tooltip-wrapper .recharts-default-tooltip {
    background: #fff !important;
    border: 1.5px solid var(--glass-border) !important;
    border-radius: 10px !important;
    color: var(--text-primary) !important;
    box-shadow: 0 4px 16px rgba(11, 31, 64, 0.12) !important;
  }

  /* Table */
  .dash-table-wrap {
    max-height: 480px;
    overflow-y: auto;
    overflow-x: auto;
    border-radius: var(--radius-md);
    border: 1.5px solid var(--glass-border);
  }

  .dash-table-wrap::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  .dash-table-wrap::-webkit-scrollbar-track {
    background: rgba(15, 42, 85, 0.05);
    border-radius: 3px;
  }

  .dash-table-wrap::-webkit-scrollbar-thumb {
    background: rgba(56,189,248,0.4);
    border-radius: 3px;
  }

  .dash-table-wrap::-webkit-scrollbar-thumb:hover {
    background: rgba(56,189,248,0.6);
  }

  .dash-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    table-layout: fixed;
  }

  .dash-table thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .dash-table thead tr {
    background: linear-gradient(90deg, #f8fafc, #f1f5f9);
  }

  .dash-table thead th {
    padding: 14px 14px;
    font-size: 11px;
    font-weight: 700;
    color: #0284c7;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    white-space: nowrap;
    border-bottom: 2px solid var(--accent-cyan);
  }

  .dash-table tbody tr {
    border-bottom: 1px solid var(--glass-border);
    transition: background-color 0.2s ease;
  }

  .dash-table tbody tr:hover {
    background: rgba(56,189,248,0.06);
  }

  .dash-table tbody tr:nth-child(even) {
    background: rgba(15, 42, 85, 0.02);
  }

  .dash-table tbody tr:nth-child(even):hover {
    background: rgba(56,189,248,0.06);
  }

  .dash-table tbody td {
    padding: 12px 14px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dash-table tbody td:first-child {
    color: #0284c7;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
  }

  /* Animations */
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .stat-card, .section-card {
    animation: fadeInUp 0.5s ease forwards;
  }

  .stat-card:nth-child(1) { animation-delay: 0.05s; }
  .stat-card:nth-child(2) { animation-delay: 0.1s; }
  .stat-card:nth-child(3) { animation-delay: 0.15s; }
  .stat-card:nth-child(4) { animation-delay: 0.2s; }

  /* Loading Spinner Styles */
  .dashboard-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    width: 100%;
    background: rgba(255, 255, 255, 0.8);
    border-radius: var(--radius-lg);
    margin-bottom: 24px;
  }

  .custom-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(56, 189, 248, 0.1);
    border-left-color: var(--accent-cyan);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Responsive tweaks */
  @media (max-width: 1100px) {
    .stat-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 700px) {
    .stat-grid { grid-template-columns: 1fr; }
    .dash-header { flex-direction: column; gap: 16px; }
    .dash-header-controls { flex-direction: column; }
  }

  /* ── Branch-wise Section Styles ── */
  .bws-tabs {
    display: flex;
    gap: 4px;
    background: rgba(15,42,85,0.04);
    padding: 5px;
    border-radius: 12px;
    border: 1.5px solid var(--glass-border);
    width: fit-content;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }
  .bws-tab {
    padding: 7px 18px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    border-radius: 8px;
    cursor: pointer;
    border: none;
    background: transparent;
    transition: all 0.2s ease;
    white-space: nowrap;
    font-family: 'Sora', sans-serif;
    letter-spacing: 0.3px;
  }
  .bws-tab.active {
    background: linear-gradient(135deg, #38BDF8, #0ea5e9);
    color: #fff;
    box-shadow: 0 2px 12px rgba(56,189,248,0.4);
  }
  .bws-tab:hover:not(.active) {
    background: rgba(56,189,248,0.08);
    color: var(--text-primary);
  }
  .bws-filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 18px;
  }
  .bws-branch-filter {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .bws-branch-chip {
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 999px;
    border: 1.5px solid var(--glass-border);
    background: #fff;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Sora', sans-serif;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .bws-branch-chip.active {
    border-color: #38BDF8;
    background: rgba(56,189,248,0.12);
    color: #0284c7;
    box-shadow: 0 2px 8px rgba(56,189,248,0.2);
  }
  .bws-branch-chip:hover:not(.active) {
    border-color: rgba(56,189,248,0.4);
    background: rgba(56,189,248,0.04);
  }
  .bws-search {
    padding: 8px 14px;
    font-size: 12px;
    font-family: 'Sora', sans-serif;
    border: 1.5px solid var(--glass-border);
    border-radius: 8px;
    color: var(--text-primary);
    outline: none;
    transition: all 0.2s ease;
    background: #fff;
    min-width: 200px;
  }
  .bws-search::placeholder { color: var(--text-muted); }
  .bws-search:focus {
    border-color: #38BDF8;
    box-shadow: 0 0 0 3px rgba(56,189,248,0.15);
  }
  .bws-table-wrap {
    border-radius: 12px;
    border: 1.5px solid var(--glass-border);
    overflow: auto;
    max-height: 420px;
    position: relative;
  }
  .bws-table-wrap::-webkit-scrollbar { width: 5px; height: 5px; }
  .bws-table-wrap::-webkit-scrollbar-track { background: rgba(15,42,85,0.04); }
  .bws-table-wrap::-webkit-scrollbar-thumb {
    background: rgba(56,189,248,0.35);
    border-radius: 3px;
  }
  .bws-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
    table-layout: auto;
    min-width: 600px;
  }
  .bws-table thead {
    position: sticky;
    top: 0;
    z-index: 3;
  }
  .bws-table thead tr {
    background: linear-gradient(90deg, #f0f7ff, #e8f4fd);
  }
  .bws-table thead th {
    padding: 12px 14px;
    font-size: 10.5px;
    font-weight: 700;
    color: #0284c7;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    white-space: nowrap;
    border-bottom: 2px solid #38BDF8;
    text-align: left;
  }
  .bws-table thead th.bws-center { text-align: center; }
  .bws-table tbody tr {
    border-bottom: 1px solid rgba(15,42,85,0.07);
    transition: background 0.15s ease;
  }
  .bws-table tbody tr:hover { background: rgba(56,189,248,0.05); }
  .bws-table tbody tr:nth-child(even) { background: rgba(15,42,85,0.015); }
  .bws-table tbody tr:nth-child(even):hover { background: rgba(56,189,248,0.05); }
  .bws-table tbody td {
    padding: 10px 14px;
    color: var(--text-secondary);
    white-space: nowrap;
  }
  .bws-table tbody td.bws-center { text-align: center; }
  .bws-table tfoot tr {
    background: linear-gradient(90deg, #f0f7ff, #e8f4fd);
    border-top: 2px solid #38BDF8;
  }
  .bws-table tfoot td {
    padding: 10px 14px;
    font-weight: 700;
    font-size: 12px;
    color: #0B1F40;
    white-space: nowrap;
  }
  .branch-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;
  }
  .bws-num-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
    min-width: 44px;
  }
  .bws-num-pill.p-total  { background: rgba(56,189,248,0.12); color: #0284c7; }
  .bws-num-pill.p-month  { background: rgba(139,92,246,0.12); color: #7c3aed; }
  .bws-num-pill.p-week   { background: rgba(244,185,66,0.12); color: #b45309; }
  .bws-num-pill.p-today  { background: rgba(16,185,129,0.12); color: #059669; }
  .bws-num-pill.p-zero   { background: rgba(15,42,85,0.05); color: #94a3b8; }
  .bws-pct-bar-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 110px;
  }
  .bws-pct-bar-track {
    flex: 1;
    height: 6px;
    background: rgba(15,42,85,0.08);
    border-radius: 3px;
    overflow: hidden;
  }
  .bws-pct-bar-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.6s ease;
  }
  .bws-pct-label {
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    color: var(--text-secondary);
    min-width: 38px;
    text-align: right;
  }
  .bws-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 14px;
    flex-wrap: wrap;
    gap: 10px;
  }
  .bws-page-info {
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 500;
  }
  .bws-page-btns {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .bws-page-btn {
    padding: 5px 12px;
    font-size: 12px;
    font-family: 'Sora', sans-serif;
    font-weight: 600;
    border: 1.5px solid var(--glass-border);
    border-radius: 7px;
    background: #fff;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .bws-page-btn.active {
    background: #38BDF8;
    border-color: #38BDF8;
    color: #fff;
    box-shadow: 0 2px 8px rgba(56,189,248,0.35);
  }
  .bws-page-btn:hover:not(.active):not(:disabled) {
    border-color: #38BDF8;
    color: #0284c7;
  }
  .bws-page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .bws-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: var(--text-muted);
    gap: 12px;
  }
  .bws-empty-icon { font-size: 36px; opacity: 0.4; }
  .bws-empty-text { font-size: 13px; font-weight: 500; }
  .bws-legend {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }
  .bws-legend-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: var(--text-muted);
    font-weight: 500;
  }
  .bws-legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
`;

/* ── Branch colour palette ───────────────────────────────────── */
const BRANCH_COLORS = [
  "#38BDF8", "#8B5CF6", "#10B981", "#F4B942",
  "#F43F5E", "#06B6D4", "#A78BFA", "#34D399",
  "#FBBF24", "#FB7185",
];

const BWS_PAGE_SIZE = 8;

const BWS_TABS = [
  // { key: "overview",           label: "📊 Overview"             },
  { key: "studentAdmission", label: "🎓 Admissions" },
  { key: "loginDetail", label: "🔑 Logins" },
  { key: "followupEnquiry", label: "📞 Follow-ups" },
  { key: "frontdeskEnquiry", label: "🪪 Front Desk" },
  { key: "studentAttendance", label: "📅 Student Attendance" },
  { key: "employeeAttendance", label: "👤 Employee Attendance" },
];

/* ── Small reusable helpers ─────────────────────────────────── */
const BwsPill = ({ value, type = "total" }) => {
  const cls = Number(value) === 0 ? "p-zero" : `p-${type}`;
  return <span className={`bws-num-pill ${cls}`}>{value ?? 0}</span>;
};

const BwsPctBar = ({ pct = 0, color = "#38BDF8" }) => (
  <div className="bws-pct-bar-wrap">
    <div className="bws-pct-bar-track">
      <div className="bws-pct-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
    </div>
    <span className="bws-pct-label">{(pct || 0).toFixed(1)}%</span>
  </div>
);

/* ── BranchWiseSection ───────────────────────────────────────── */
const BranchWiseSection = ({ branches = [], selectedSession, isMaster }) => {
  const [activeTab, setActiveTab] = useState("studentAdmission");
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [localBranchWiseData, setLocalBranchWiseData] = useState({
    studentAdmission: [],
    loginDetail: [],
    studentAttendance: [],
    followupEnquiry: [],
    frontdeskEnquiry: [],
    employeeAttendance: []
  });
  const [loading, setLoading] = useState(false);
  const [selectedApiBranches, setSelectedApiBranches] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch data based on user type
  const fetchBranchWiseData = async (branchIds) => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/DashboardTotalNumbers/Dashboard_AllModules?SessionId=${selectedSession}&BranchIds=${branchIds.join(",")}`,
        { method: "POST", headers: { "Content-Type": "application/json", Authorization: token } }
      );
      if (response.ok) {
        const data = await response.json();
        setLocalBranchWiseData({
          studentAdmission: data.studentAdmission || [],
          loginDetail: data.loginDetail || [],
          studentAttendance: data.studentAttendance || [],
          followupEnquiry: data.followupEnquiry || [],
          frontdeskEnquiry: data.frontdeskEnquiry || [],
          employeeAttendance: data.employeeAttendance || []
        });
      }
    } catch (error) {
      console.error("Error fetching branch-wise data:", error);
    } finally {
      setLoading(false);
    }
  };

  // On mount, fetch data based on user type
  useEffect(() => {
    if (isMaster && branches.length > 0) {
      const allBranchIds = branches.map(b => b.branchId);
      fetchBranchWiseData(allBranchIds);
      setSelectedApiBranches(allBranchIds);
    } else if (!isMaster) {
      const branchId = sessionStorage.getItem("branchId");
      fetchBranchWiseData([branchId]);
      setSelectedApiBranches([branchId]);
    }
  }, [branches, isMaster]);

  // Toggle branch selection for master users
  const handleApiBranchToggle = (branchId) => {
    if (!isMaster) return;
    setSelectedApiBranches(prev =>
      prev.includes(branchId)
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    );
  };

  // Select all branches for master users
  const handleSelectAllBranches = () => {
    if (!isMaster) return;
    const allBranchIds = branches.map(b => b.branchId);
    setSelectedApiBranches(allBranchIds);
    fetchBranchWiseData(allBranchIds);
  };

  // Apply branch filter for master users
  const handleApplyBranchFilter = () => {
    if (!isMaster) return;
    fetchBranchWiseData(selectedApiBranches);
  };

  // Get all unique branch names
  const allBranchNames = useMemo(() => {
    const set = new Set();
    Object.values(localBranchWiseData).forEach(arr =>
      (arr || []).forEach(r => r.branchName && set.add(r.branchName.trim()))
    );
    return [...set];
  }, [localBranchWiseData]);

  // Get color for branch
  const colorFor = (name) =>
    BRANCH_COLORS[allBranchNames.indexOf((name || "").trim()) % BRANCH_COLORS.length];

  // Toggle branch filter
  const toggleBranch = (name) => {
    setPage(1);
    setSelectedBranches(prev =>
      prev.includes(name) ? prev.filter(b => b !== name) : [...prev, name]
    );
  };

  // Filter rows based on search and selected branches
  const filterRows = (arr) => {
    let rows = arr || [];
    if (selectedBranches.length > 0)
      rows = rows.filter(r => selectedBranches.includes((r.branchName || "").trim()));
    if (search.trim())
      rows = rows.filter(r =>
        (r.branchName || "").toLowerCase().includes(search.toLowerCase())
      );
    return rows;
  };

  // Paginate rows
  const paginate = (rows) => {
    const total = Math.ceil(rows.length / BWS_PAGE_SIZE) || 1;
    const current = Math.min(page, total);
    return {
      rows: rows.slice((current - 1) * BWS_PAGE_SIZE, current * BWS_PAGE_SIZE),
      total,
      current,
    };
  };

  // Handle tab change
  const handleTab = (key) => { setActiveTab(key); setPage(1); };

  // Pagination component
  const PaginationBar = ({ total, current }) => {
    if (total <= 1) return null;
    return (
      <div className="bws-pagination">
        <span className="bws-page-info">Page {current} of {total}</span>
        <div className="bws-page-btns">
          <button className="bws-page-btn" disabled={current <= 1} onClick={() => setPage(current - 1)}>‹ Prev</button>
          {Array.from({ length: total }, (_, i) => i + 1).map(p => (
            <button key={p} className={`bws-page-btn ${p === current ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="bws-page-btn" disabled={current >= total} onClick={() => setPage(current + 1)}>Next ›</button>
        </div>
      </div>
    );
  };

  // Simple table component
  const SimpleTable = ({ data, emptyMsg }) => {
    const filtered = filterRows(data);
    const { rows, total, current } = paginate(filtered);
    const sum = (key) => filtered.reduce((s, r) => s + (r[key] || 0), 0);
    return (
      <>
        <div className="bws-table-wrap">
          <table className="bws-table">
            <thead>
              <tr>
                <th style={{ minWidth: 180 }}>Branch</th>
                <th className="bws-center">Total</th>
                <th className="bws-center">This Month</th>
                <th className="bws-center">This Week</th>
                <th className="bws-center">Today</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5}>
                  <div className="bws-empty">
                    <span className="bws-empty-icon">⏳</span>
                    <span className="bws-empty-text">Loading data...</span>
                  </div>
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5}>
                  <div className="bws-empty">
                    <span className="bws-empty-icon">🏫</span>
                    <span className="bws-empty-text">{emptyMsg || "No data available"}</span>
                  </div>
                </td></tr>
              ) : rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="branch-dot" style={{ backgroundColor: colorFor(r.branchName) }} />
                      <strong style={{ color: "#0B1F40" }}>{r.branchName}</strong>
                    </span>
                  </td>
                  <td className="bws-center"><BwsPill value={r.total} type="total" /></td>
                  <td className="bws-center"><BwsPill value={r.month} type="month" /></td>
                  <td className="bws-center"><BwsPill value={r.week} type="week" /></td>
                  <td className="bws-center"><BwsPill value={r.today} type="today" /></td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && !loading && (
              <tfoot>
                <tr>
                  <td>Σ Total ({filtered.length} branches)</td>
                  <td style={{ textAlign: "center" }}>{sum("total")}</td>
                  <td style={{ textAlign: "center" }}>{sum("month")}</td>
                  <td style={{ textAlign: "center" }}>{sum("week")}</td>
                  <td style={{ textAlign: "center" }}>{sum("today")}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <PaginationBar total={total} current={current} />
      </>
    );
  };

  // Attendance table component
  const AttendanceTable = ({ data, emptyMsg }) => {
    const filtered = filterRows(data);
    const { rows, total, current } = paginate(filtered);
    return (
      <>
        <div className="bws-table-wrap">
          <table className="bws-table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ minWidth: 170, verticalAlign: "middle" }}>Branch</th>
                <th colSpan={3} className="bws-center" style={{ borderLeft: "2px solid rgba(56,189,248,0.25)" }}>Overall</th>
                <th colSpan={3} className="bws-center" style={{ borderLeft: "2px solid rgba(139,92,246,0.25)" }}>This Month</th>
                <th colSpan={2} className="bws-center" style={{ borderLeft: "2px solid rgba(244,185,66,0.25)" }}>Today</th>
              </tr>
              <tr>
                <th className="bws-center" style={{ borderLeft: "2px solid rgba(56,189,248,0.25)" }}>Present</th>
                <th className="bws-center">Absent</th>
                <th className="bws-center">Week Off</th>
                <th className="bws-center" style={{ borderLeft: "2px solid rgba(139,92,246,0.25)" }}>Present %</th>
                <th className="bws-center">Absent %</th>
                <th className="bws-center">Total</th>
                <th className="bws-center" style={{ borderLeft: "2px solid rgba(244,185,66,0.25)" }}>Present</th>
                <th className="bws-center">Absent</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}>
                  <div className="bws-empty">
                    <span className="bws-empty-icon">⏳</span>
                    <span className="bws-empty-text">Loading data...</span>
                  </div>
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9}>
                  <div className="bws-empty">
                    <span className="bws-empty-icon">📅</span>
                    <span className="bws-empty-text">{emptyMsg || "No attendance data"}</span>
                  </div>
                </td></tr>
              ) : rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="branch-dot" style={{ backgroundColor: colorFor(r.branchName) }} />
                      <strong style={{ color: "#0B1F40" }}>{r.branchName}</strong>
                    </span>
                  </td>
                  <td className="bws-center"><BwsPill value={r.totalPresent} type="total" /></td>
                  <td className="bws-center"><BwsPill value={r.totalAbsent} type="total" /></td>
                  <td className="bws-center"><BwsPill value={r.totalWeekOff} type="week" /></td>
                  <td className="bws-center" style={{ borderLeft: "2px solid rgba(139,92,246,0.1)" }}>
                    <BwsPctBar pct={r.monthPresentPercent || 0} color={
                      (r.monthPresentPercent || 0) >= 80 ? "#10B981" :
                        (r.monthPresentPercent || 0) >= 60 ? "#F4B942" : "#F43F5E"
                    } />
                  </td>
                  <td className="bws-center">
                    <BwsPctBar pct={r.monthAbsentPercent || 0} color="#F43F5E" />
                  </td>
                  <td className="bws-center"><BwsPill value={r.monthTotal} type="month" /></td>
                  <td className="bws-center" style={{ borderLeft: "2px solid rgba(244,185,66,0.1)" }}>
                    <BwsPill value={r.todayPresent} type="today" />
                  </td>
                  <td className="bws-center"><BwsPill value={r.todayAbsent} type="today" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar total={total} current={current} />
      </>
    );
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Selected label for dropdown
  const selectedLabel = selectedApiBranches.length === 0 || selectedApiBranches.length === branches.length
    ? "All Branches"
    : `${selectedApiBranches.length} Branch${selectedApiBranches.length > 1 ? "es" : ""} Selected`;

  return (
    <div className="section-card" style={{ marginBottom: 28 }}>
      <div className="section-card-header" style={{ flexWrap: "wrap", gap: 12 }}>
        <div className="section-title-wrap">
          <div className="section-icon"><i className="bi bi-building"></i></div>
          <h4 className="section-title">Branch-wise Detailed Statistics</h4>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {/* Branch selection dropdown for master users */}
          {isMaster && (
            <div ref={dropdownRef} style={{ position: "relative" }}>
              <button
                onClick={() => setDropdownOpen(prev => !prev)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 14px", fontSize: 13, fontWeight: 600,
                  fontFamily: "Sora, sans-serif",
                  background: "#fff", border: "1.5px solid var(--glass-border)",
                  borderRadius: "var(--radius-sm)", cursor: "pointer",
                  color: "var(--text-primary)", transition: "all 0.2s ease",
                  minWidth: 200,
                  boxShadow: dropdownOpen ? "0 0 0 3px rgba(56,189,248,0.15)" : "none",
                  borderColor: dropdownOpen ? "var(--accent-cyan)" : "var(--glass-border)",
                }}
              >
                <i className="bi bi-diagram-3" style={{ color: "#38BDF8" }}></i>
                {selectedLabel}
                <span style={{ marginLeft: "auto", color: "#38BDF8", fontSize: 11 }}>
                  {dropdownOpen ? "▲" : "▼"}
                </span>
              </button>

              {dropdownOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 6px)", right: 0,
                  background: "#fff", border: "1.5px solid var(--glass-border)",
                  borderRadius: "var(--radius-md)", boxShadow: "0 8px 32px rgba(11,31,64,0.14)",
                  zIndex: 100, minWidth: 240, padding: "10px 0", maxHeight: 320, overflowY: "auto"
                }}>
                  <div
                    onClick={handleSelectAllBranches}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "9px 16px", cursor: "pointer", fontSize: 13,
                      fontWeight: selectedApiBranches.length === 0 ? 700 : 500,
                      color: selectedApiBranches.length === 0 ? "#0284c7" : "var(--text-secondary)",
                      background: selectedApiBranches.length === 0 ? "rgba(56,189,248,0.08)" : "transparent",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(56,189,248,0.06)"}
                    onMouseLeave={e => e.currentTarget.style.background = selectedApiBranches.length === 0 ? "rgba(56,189,248,0.08)" : "transparent"}
                  >
                    <span style={{
                      width: 16, height: 16, borderRadius: 4, border: "2px solid",
                      borderColor: selectedApiBranches.length === 0 ? "#38BDF8" : "#cbd5e1",
                      background: selectedApiBranches.length === 0 ? "#38BDF8" : "transparent",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, color: "#fff", flexShrink: 0,
                    }}>
                      {selectedApiBranches.length === 0 && "✓"}
                    </span>
                    All Branches
                  </div>

                  <div style={{ height: 1, background: "var(--glass-border)", margin: "6px 12px" }} />

                  {branches.map((branch) => {
                    const isChecked = selectedApiBranches.includes(branch.branchId);
                    const dotColor = BRANCH_COLORS[branches.indexOf(branch) % BRANCH_COLORS.length];
                    return (
                      <div
                        key={branch.branchId}
                        onClick={() => handleApiBranchToggle(branch.branchId)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "9px 16px", cursor: "pointer", fontSize: 13,
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked ? "#0284c7" : "var(--text-secondary)",
                          background: isChecked ? "rgba(56,189,248,0.06)" : "transparent",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(56,189,248,0.06)"}
                        onMouseLeave={e => e.currentTarget.style.background = isChecked ? "rgba(56,189,248,0.06)" : "transparent"}
                      >
                        <span style={{
                          width: 16, height: 16, borderRadius: 4, border: "2px solid",
                          borderColor: isChecked ? "#38BDF8" : "#cbd5e1",
                          background: isChecked ? "#38BDF8" : "transparent",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, color: "#fff", flexShrink: 0,
                        }}>
                          {isChecked && "✓"}
                        </span>
                        <span style={{
                          width: 8, height: 8, borderRadius: "50%",
                          background: dotColor, flexShrink: 0,
                        }} />
                        {branch.branchName}
                      </div>
                    );
                  })}

                  <div style={{ padding: "10px 12px 4px", borderTop: "1px solid var(--glass-border)", marginTop: 6 }}>
                    <button
                      onClick={() => { handleApplyBranchFilter(); setDropdownOpen(false); }}
                      style={{
                        width: "100%", padding: "8px", fontSize: 13, fontWeight: 700,
                        fontFamily: "Sora, sans-serif", borderRadius: 8, border: "none",
                        background: "linear-gradient(135deg, #38BDF8, #0ea5e9)",
                        color: "#fff", cursor: "pointer",
                        boxShadow: "0 2px 10px rgba(56,189,248,0.35)",
                      }}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legend */}
          <div className="bws-legend">
            <div className="bws-legend-item"><span className="bws-legend-dot" style={{ background: "#38BDF8" }} />Total</div>
            <div className="bws-legend-item"><span className="bws-legend-dot" style={{ background: "#8B5CF6" }} />Month</div>
            <div className="bws-legend-item"><span className="bws-legend-dot" style={{ background: "#F4B942" }} />Week</div>
            <div className="bws-legend-item"><span className="bws-legend-dot" style={{ background: "#10B981" }} />Today</div>
          </div>
        </div>
      </div>

      <div className="section-body">
        {/* Tab strip */}
        <div className="bws-tabs">
          {BWS_TABS.map(t => (
            <button
              key={t.key}
              className={`bws-tab ${activeTab === t.key ? "active" : ""}`}
              onClick={() => handleTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filter bar (table-level name search + chip filter) */}
        <div className="bws-filter-bar">
          <div className="bws-branch-filter">
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginRight: 4 }}>Filter:</span>
            <button
              className={`bws-branch-chip ${selectedBranches.length === 0 ? "active" : ""}`}
              onClick={() => { setSelectedBranches([]); setPage(1); }}
            >All</button>
            {allBranchNames.map((name) => (
              <button
                key={name}
                className={`bws-branch-chip ${selectedBranches.includes(name) ? "active" : ""}`}
                onClick={() => toggleBranch(name)}
                style={selectedBranches.includes(name) ? { borderColor: colorFor(name) } : {}}
              >
                <span className="branch-dot" style={{ backgroundColor: colorFor(name) }} />
                {name}
              </button>
            ))}
          </div>
          <input
            className="bws-search"
            placeholder="🔍  Search branch..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {/* Tab content */}
        {activeTab === "studentAdmission" && <SimpleTable data={localBranchWiseData.studentAdmission} emptyMsg="No admission data available" />}
        {activeTab === "loginDetail" && <SimpleTable data={localBranchWiseData.loginDetail} emptyMsg="No login data available" />}
        {activeTab === "followupEnquiry" && <SimpleTable data={localBranchWiseData.followupEnquiry} emptyMsg="No follow-up data available" />}
        {activeTab === "frontdeskEnquiry" && <SimpleTable data={localBranchWiseData.frontdeskEnquiry} emptyMsg="No front desk data available" />}
        {activeTab === "studentAttendance" && <AttendanceTable data={localBranchWiseData.studentAttendance} emptyMsg="No student attendance data" />}
        {activeTab === "employeeAttendance" && <AttendanceTable data={localBranchWiseData.employeeAttendance} emptyMsg="No employee attendance data" />}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
═══════════════════════════════════════════════════════════════ */
const Dashboard = () => {
  const navigate = useNavigate();
const [rawAttendanceRecords, setRawAttendanceRecords] = useState([]);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
const [loginModalData, setLoginModalData] = useState([]);
const [loginModalLoading, setLoginModalLoading] = useState(false);

  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);

  const [admissionData, setAdmissionData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [duesData, setDuesData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [viewFrequency, setViewFrequency] = useState("monthly");
  const [ddlClass, setDdlClass] = useState([]);
  const [amountData, setAmountData] = useState({});
  const [collectionData, setCollectionData] = useState([]);
  const [collectionViewFrequency, setCollectionViewFrequency] = useState("daily");
  const [schoolData, setSchoolData] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedSession, setSelectedSession] = useState("0");
  const [registrationData, setRegistrationData] = useState({});
  const [todayAttendancePie, setTodayAttendancePie] = useState([]);
  const [attendancePieLabel, setAttendancePieLabel] = useState("Overall Attendance");

  // Loading state for data section only
  const [dataLoading, setDataLoading] = useState(false);

  const [branchWiseData, setBranchWiseData] = useState({
    studentAdmission: [],
    loginDetail: [],
    studentAttendance: [],
    followupEnquiry: [],
    frontdeskEnquiry: [],
    employeeAttendance: []
  });

  const [dashData, setDashData] = useState({
    totalAdmission: 0,
    totalTodayAdmission: 0,
    totalWeeklyAdmission: 0,
    totalMonthlyAdmission: 0,
    totalLogin: 0,
    todayLogin: 0,
    weeklyLogin: 0,
    monthlyLogin: 0,
    todaysFollowUp: 0,
    missedFollowUp: 0,
    totalFollowUp: 0,
    totalDue: 0,
    totalTodayDue: 0,
    totalWeeklyDue: 0,
    totalMonthlyDue: 0,
  });


  // Admissions → navigate to admissions page
const handleAdmissionClick = () => navigate("/Admission/FrmAdmissionDetails.aspx/MQ=="); // adjust route

// Follow Ups → navigate to follow-ups page  
const handleFollowUpClick = () => navigate("/Enquiry/EnquiryFollowUp.aspx/ODc="); // adjust route

// Dues → navigate to dues/fees page
const handleDuesClick = () => navigate("/Dues%20List/MTA4"); // adjust route

// Login → fetch modal data + open modal
const handleLoginClick = async () => {
  setLoginModalOpen(true);
  setLoginModalLoading(true);
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
    const response = await fetch(
      `${apiUrl}/DashboardTotalNumbers/Dashboard_StudentLogin_Details?SessionId=${selectedSession}&BranchIds=${branchId}&FilterType=Month&PageNumber=1&PageSize=50`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token
        }
      }
    );
    if (response.ok) {
      const data = await response.json();
      setLoginModalData(data?.data || []);
    }
  } catch (err) {
    console.error("Error fetching login details:", err);
  } finally {
    setLoginModalLoading(false);
  }
};


const handleClassChange = (e) => { setSelectedClass(e.target.value); };

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else { console.error("Failed to fetch branches"); }
    } catch (error) { console.error("Error fetching branches:", error); }
  };

  const fetchFinancialYears = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error fetching financial years: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") { console.error("Record Not Found"); return; }
      setFinancialYears(data.data || data);
      const today = new Date();
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;
      const financialYearStart = new Date(currentYear, 3, 1);
      const financialYearEnd = new Date(nextYear, 2, 31);
      let currentSessionString;
      if (today >= financialYearStart && today <= financialYearEnd) {
        currentSessionString = `${currentYear}-${nextYear}`;
      } else {
        currentSessionString = `${currentYear - 1}-${currentYear}`;
      }
      const currentSession = (data.data || data).find((item) => item.finanacialYear === currentSessionString);
      if (currentSession) {
        setSelectedSession(currentSession.financialYearID);
        setRegistrationData((prevData) => ({ ...prevData, financialYearID: currentSession.financialYearID }));
      }
    } catch (error) { console.error(error); }
  };

  const fetchSchoolData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: sessionStorage.getItem("token") },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        const schoolData = data[0];
        sessionStorage.setItem("schoolName", JSON.stringify(schoolData.schoolName));
        sessionStorage.setItem("address", JSON.stringify(schoolData.address));
        sessionStorage.setItem("phoneNo", JSON.stringify(schoolData.phoneNo));
        sessionStorage.setItem("logoImg", JSON.stringify(schoolData.logoImg));
        setSchoolData(schoolData);
      } else { console.error("Failed to fetch school data"); }
    } catch (error) { console.error("API request error:", error); }
  };

  const fetchDashboardAllModules = async () => {
    try {
      setDataLoading(true); // Start loading
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(
        `${apiUrl}/DashboardTotalNumbers/Dashboard_AllModules?SessionId=${selectedSession}&BranchIds=${branchId}`,
        { method: "POST", headers: { "Content-Type": "application/json", Authorization: token } }
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Dashboard All Modules Response:", data);
        if (isMaster) {
          setBranchWiseData({
            studentAdmission: data.studentAdmission || [],
            loginDetail: data.loginDetail || [],
            studentAttendance: data.studentAttendance || [],
            followupEnquiry: data.followupEnquiry || [],
            frontdeskEnquiry: data.frontdeskEnquiry || [],
            employeeAttendance: data.employeeAttendance || []
          });
        }
        if (data.studentAdmission && data.studentAdmission.length > 0) {
          const branchData = data.studentAdmission[0];
          setDashData(prev => ({
            ...prev,
            totalAdmission: branchData.total || 0,
            totalTodayAdmission: branchData.today || 0,
            totalWeeklyAdmission: branchData.week || 0,
            totalMonthlyAdmission: branchData.month || 0
          }));
        }
        if (data.loginDetail && data.loginDetail.length > 0) {
          const loginData = data.loginDetail[0];
          setDashData(prev => ({
            ...prev,
            totalLogin: loginData.total || 0,
            todayLogin: loginData.today || 0,
            weeklyLogin: loginData.week || 0,
            monthlyLogin: loginData.month || 0
          }));
        }
        if (data.studentAttendance && data.studentAttendance.length > 0) {
          const a = data.studentAttendance[0];

          // Prefer today → month → overall (whichever has data)
          let present, absent, label;

          if (a.todayPresent > 0 || a.todayAbsent > 0) {
            present = a.todayPresent;
            absent = a.todayAbsent;
            label = "Today's Attendance";
          } else if (a.monthPresent > 0 || a.monthAbsent > 0) {
            present = a.monthPresent;
            absent = a.monthAbsent;
            label = "This Month's Attendance";
          } else {
            present = a.totalPresent;
            absent = a.totalAbsent;
            label = "Overall Attendance";
          }

          setTodayAttendancePie([
            { name: "Present", value: present },
            { name: "Absent", value: absent },
          ]);
          setAttendancePieLabel(label); // to show which period is displayed
        }
        if (data.followupEnquiry && data.followupEnquiry.length > 0) {
          const followupData = data.followupEnquiry[0];
          setDashData(prev => ({
            ...prev,
            todaysFollowUp: followupData.today || 0,
            totalFollowUp: followupData.total || 0,
            missedFollowUp: (followupData.total - followupData.today) || 0
          }));
        }
      } else {
        console.error("Failed to fetch dashboard data:", response.status);
        setSnackbarMessage('Failed to load dashboard data');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbarMessage('Error loading dashboard data');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setDataLoading(false); // Stop loading
    }
  };

  const fetchMonthDues = async (sessionId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(`${apiUrl}/Dashboard/MonthDuesList?Branch_Id=${branchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ sessionId: parseInt(sessionId) }),
      });
      if (!response.ok) throw new Error(`Error fetching dues: ${response.status}`);
      const data = await response.json();
      if (!data || data.length === 0) {
        setDuesData([]);
        setDashData(prev => ({ ...prev, totalDue: 0, totalTodayDue: 0, totalWeeklyDue: 0, totalMonthlyDue: 0 }));
        return;
      }
      setDuesData(data);
      const totalDue = data.reduce((sum, item) => sum + (item.totalFee - item.paidAmount), 0);
      const today = new Date();
      const todayDues = data.filter(item => { const d = new Date(item.paymentDate); return d.toDateString() === today.toDateString(); });
      const totalTodayDue = todayDues.reduce((sum, item) => sum + (item.totalFee - item.paidAmount), 0);
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const weekDues = data.filter(item => { const d = new Date(item.paymentDate); return d >= weekAgo && d <= today; });
      const totalWeeklyDue = weekDues.reduce((sum, item) => sum + (item.totalFee - item.paidAmount), 0);
      const monthDues = data.filter(item => { const d = new Date(item.paymentDate); return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); });
      const totalMonthlyDue = monthDues.reduce((sum, item) => sum + (item.totalFee - item.paidAmount), 0);
      setDashData(prev => ({ ...prev, totalDue, totalTodayDue, totalWeeklyDue, totalMonthlyDue }));
    } catch (error) {
      console.error(error);
      setDuesData([]);
      setDashData(prev => ({ ...prev, totalDue: 0, totalTodayDue: 0, totalWeeklyDue: 0, totalMonthlyDue: 0 }));
    }
  };

 const handleFrequencyChange = (e) => {
  setViewFrequency(e.target.value);
  fetchAttendanceData(e.target.value);
};

  const fetchAttendanceData = async (timeFrame) => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
    const filterTypeMap = { "daily": "Today", "weekly": "Week", "monthly": "Month" };
    const requestBody = {
      BranchId: parseInt(branchId),
      SessionId: parseInt(selectedSession),
      ModuleType: "Student",
      Type: "",
      FilterType: filterTypeMap[timeFrame] || "Month",
      PageNumber: 1,
      PageSize: 1000
    };
    const response = await fetch(`${apiUrl}/DashboardTotalNumbers/GetAttendanceDetails`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify(requestBody),
    });
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data && data.data.length > 0) {
        setRawAttendanceRecords(data.data);
      } else {
        setRawAttendanceRecords([]);
      }
    } else {
      setRawAttendanceRecords([]);
      console.error("Failed to fetch attendance data");
    }
  } catch (error) {
    setRawAttendanceRecords([]);
    console.error("API request error:", error);
  }
}; 

  const processAttendanceForChart = (records, timeFrame) => {
    const grouped = {};
    records.forEach(record => {
      const date = new Date(record.attendanceDate);
      let key;
      if (timeFrame === 'daily') { key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
      else if (timeFrame === 'weekly') { const d = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; key = d[date.getDay()]; }
      else { key = `Day ${date.getDate()}`; }
      if (!grouped[key]) { grouped[key] = { day: key, presentStudentsCount: 0, absentStudentsCount: 0 }; }
      if (record.attendanceStatus === 1) { grouped[key].presentStudentsCount++; }
      else if (record.attendanceStatus === 0) { grouped[key].absentStudentsCount++; }
    });
    return Object.values(grouped);
  };

  const handleAmountFrequencyChange = (e) => {
    setCollectionViewFrequency(e.target.value);
    fetchAmountData(e.target.value);
  };

  const fetchAmountData = async (timeFrame) => {
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
    const endpointMap = {
      "monthly": "/DashboardTotalNumbers/Amountcollection_CurrentMonth",
      "weekly": "/DashboardTotalNumbers/Amountcollection_CurrentWeek",
      "daily": "/DashboardTotalNumbers/Amountcollection_CurrentYear"
    };
    const apiUrl = `${process.env.REACT_APP_BASE_URL}${endpointMap[timeFrame]}?Branch_Id=${branchId}`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        if (!data || !data.data || data.data.length === 0) {
          setSnackbarMessage('No collection data found');
          setOpenSnackbar(true);
          setSnackbarSeverity("warning");
          setCollectionData([]);
          return;
        }
        setCollectionData(data.data || []);
      } else {
        console.error("Failed to fetch collection data");
        setCollectionData([]);
      }
    } catch (error) {
      console.error("API request error:", error);
      setCollectionData([]);
    }
  };

  const fetchClass = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName?Branch_Id=${branchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error fetching classes: ${response.status}`);
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") { setDdlClass([]); return; }
      setDdlClass(data);
    } catch (error) { console.error(error); setDdlClass([]); }
  };

  const fetchCollection = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(`${apiUrl}/DashboardTotalNumbers/Amountcollection_CurrentYear?Branch_Id=${branchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error fetching collection: ${response.status}`);
      const data = await response.json();
      if (!data || !data.data || data.data.length === 0) { setCollectionData([]); return; }
      setCollectionData(data.data || []);
    } catch (error) { console.error(error); setCollectionData([]); }
  };

  useEffect(() => {
  if (rawAttendanceRecords.length === 0) {
    setAttendanceData([]);
    return;
  }

  let filtered = rawAttendanceRecords;

  if (selectedClass) {
    const selectedClassObj = ddlClass.find(
      (c) => String(c.classId) === String(selectedClass)
    );
    const classNameToMatch = selectedClassObj?.className;
    if (classNameToMatch) {
      filtered = filtered.filter(
        (record) => record.className === classNameToMatch
      );
    }
  }

  setAttendanceData(processAttendanceForChart(filtered, viewFrequency));
}, [rawAttendanceRecords, selectedClass, viewFrequency, ddlClass]);

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);
    fetchFinancialYears();
    if (isMasterUser) { fetchBranches(); }
    else { const branchId = sessionStorage.getItem("branchId"); setSelectedBranchId(branchId); }
  }, []);

  // Handle branch change with loading
  const handleBranchChange = (e) => {
    setSelectedBranchId(e.target.value);
    // Data will be fetched in useEffect with loading state
  };

  useEffect(() => {
    if (selectedBranchId && selectedSession && selectedSession !== "0") {
      fetchDashboardAllModules();
      fetchMonthDues(selectedSession);
      fetchClass();
      fetchCollection();
      fetchAttendanceData(viewFrequency);
      fetchAmountData(collectionViewFrequency);
    }
  }, [selectedBranchId, selectedSession]);

  useEffect(() => {
    if (selectedBranchId && selectedSession && selectedSession !== "0") {
      fetchAttendanceData(viewFrequency);
    }
  }, [viewFrequency]);

  useEffect(() => {
    if (selectedBranchId && selectedSession && selectedSession !== "0") {
      fetchAmountData(collectionViewFrequency);
    }
  }, [collectionViewFrequency]);

  const handleSessionChange = (e) => {
    const selectedId = e.target.value;
    setSelectedSession(selectedId);
    setRegistrationData((prevData) => ({ ...prevData, financialYearID: selectedId }));
    if (selectedId !== "0") { fetchMonthDues(selectedId); }
  };

  const customTooltipStyle = {
    backgroundColor: '#0f2a55',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#F0F4FF',
    fontFamily: 'Sora, sans-serif',
    fontSize: '12px',
  };

  return (
    <>
      <style>{dashboardStyles}</style>

      <div className="dash-root">
        <div className="dash-content">

          {/* Header - Always visible */}
          <div className="dash-header">
            <div className="dash-title-wrap">
              <div className="dash-title-icon">
                <i className="bi bi-speedometer2" style={{ color: '#0B1F40' }}></i>
              </div>
              <div>
                <h3 className="dash-title-text">Dashboard</h3>
                <div className="dash-title-sub">OVERVIEW & ANALYTICS</div>
              </div>
            </div>

            <div className="dash-header-controls">
              {isMaster && (
                <div className="dash-select-wrap">
                  <span className="dash-select-label">Branch:</span>
                  <select
                    className="dash-select"
                    value={selectedBranchId || ""}
                    onChange={handleBranchChange}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch.branchId} value={branch.branchId}>{branch.branchName}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="dash-select-wrap">
                <span className="dash-select-label">Session:</span>
                <select
                  className="dash-select"
                  value={selectedSession}
                  onChange={handleSessionChange}
                >
                  <option value="0">Select Session</option>
                  {financialYears.map((year) => (
                    <option key={year.financialYearID} value={year.financialYearID}>{year.finanacialYear}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {dataLoading ? (
            // Loading Spinner - Only this section shows during data fetch
            <div className="dashboard-loading-container">
              <div className="custom-spinner"></div>
            </div>
          ) : (
            // All Data Sections - Hidden during loading
            <>
              {/* KPI Stat Cards */}
              <div className="stat-grid">
                {/* Login Card */}
                <div 
  className="stat-card stat-card-login" 
  onClick={handleLoginClick}
  style={{ cursor: "pointer" }}
  title="Click to view login details"
>
                  <div className="stat-card-header">
                    <span className="stat-card-title">System Logins</span>
                    <div className="stat-card-icon icon-gold">
                      <i className="mdi mdi-chart-line"></i>
                    </div>
                  </div>
                  <span className="stat-total-label">Total</span>
                  <div className="stat-total-value">{dashData.totalLogin}</div>
                  <div className="stat-grid-bottom">
                    <div className="stat-mini">
                      <span className="stat-mini-label">Today</span>
                      <span className="stat-mini-value">{dashData.todayLogin}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Week</span>
                      <span className="stat-mini-value">{dashData.weeklyLogin}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Month</span>
                      <span className="stat-mini-value">{dashData.monthlyLogin}</span>
                    </div>
                  </div>
                </div>

                {/* Admissions Card */}
              <div 
  className="stat-card stat-card-admissions" 
  onClick={handleAdmissionClick}
  style={{ cursor: "pointer" }}
  title="Click to view admissions"
>
                  <div className="stat-card-header">
                    <span className="stat-card-title">Admissions</span>
                    <div className="stat-card-icon icon-cyan">
                      <i className="mdi mdi-bookmark-outline"></i>
                    </div>
                  </div>
                  <span className="stat-total-label">Total</span>
                  <div className="stat-total-value">{dashData.totalAdmission || 0}</div>
                  <div className="stat-grid-bottom">
                    <div className="stat-mini">
                      <span className="stat-mini-label">Today</span>
                      <span className="stat-mini-value">{dashData.totalTodayAdmission || 0}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Week</span>
                      <span className="stat-mini-value">{dashData.totalWeeklyAdmission || 0}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Month</span>
                      <span className="stat-mini-value">{dashData.totalMonthlyAdmission || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Follow Ups Card */}
               <div 
  className="stat-card stat-card-followup" 
  onClick={handleFollowUpClick}
  style={{ cursor: "pointer" }}
  title="Click to view follow-ups"
>
                  <div className="stat-card-header">
                    <span className="stat-card-title">Follow Ups</span>
                    <div className="stat-card-icon icon-emerald">
                      <i className="mdi mdi-diamond"></i>
                    </div>
                  </div>
                  <span className="stat-total-label">Total</span>
                  <div className="stat-total-value">{dashData.totalFollowUp || 0}</div>
                  <div className="stat-grid-bottom">
                    <div className="stat-mini">
                      <span className="stat-mini-label">Today</span>
                      <span className="stat-mini-value">{dashData.todaysFollowUp || 0}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Missed</span>
                      <span className="stat-mini-value">{dashData.missedFollowUp || 0}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Month</span>
                      <span className="stat-mini-value">{dashData.monthlyLogin || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Due Card */}
              <div 
  className="stat-card stat-card-due" 
  onClick={handleDuesClick}
  style={{ cursor: "pointer" }}
  title="Click to view dues"
>
                  <div className="stat-card-header">
                    <span className="stat-card-title">Dues</span>
                    <div className="stat-card-icon icon-rose">
                      <i className="mdi mdi-diamond"></i>
                    </div>
                  </div>
                  <span className="stat-total-label">Total</span>
                  <div className="stat-total-value" style={{ fontSize: '24px' }}>₹{dashData.totalDue.toFixed(2)}</div>
                  <div className="stat-grid-bottom">
                    <div className="stat-mini">
                      <span className="stat-mini-label">Today</span>
                      <span className="stat-mini-value" style={{ fontSize: '12px' }}>₹{dashData.totalTodayDue.toFixed(0)}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Week</span>
                      <span className="stat-mini-value" style={{ fontSize: '12px' }}>₹{dashData.totalWeeklyDue.toFixed(0)}</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-label">Month</span>
                      <span className="stat-mini-value" style={{ fontSize: '12px' }}>₹{dashData.totalMonthlyDue.toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── BRANCH-WISE SECTION (enhanced) ── */}

              {BranchWiseSection && (
                <BranchWiseSection
                  branches={branches}
                  selectedSession={selectedSession}
                />
              )}


              {/* Attendance Chart */}
              <div className="section-card">
                <div className="section-card-header">
                  <div className="section-title-wrap">
                    <div className="section-icon"><i className="bi bi-bar-chart-fill"></i></div>
                    <h4 className="section-title">Attendance</h4>
                  </div>
                </div>
                <div className="section-body">
                  <div className="attend-controls">
                    <div className="dash-select-wrap">
                      <span className="dash-select-label">Class:</span>
                      <select
                        className="dash-select"
                        value={selectedClass}
                        onChange={handleClassChange}
                        style={{ minWidth: '160px' }}
                      >
                        <option value="">All Classes</option>
                        {ddlClass.map((item) => (
                          <option key={item.classId} value={item.classId}>{item.className}</option>
                        ))}
                      </select>
                    </div>
                    <div className="freq-controls">
                      <span className="freq-label-text">View:</span>
                      {["monthly", "weekly", "daily"].map((freq) => (
                        <div className="freq-btn" key={freq}>
                          <input
                            type="radio"
                            id={`attend-${freq}`}
                            value={freq}
                            checked={viewFrequency === freq}
                            onChange={handleFrequencyChange}
                          />
                          <label htmlFor={`attend-${freq}`}>
                            {freq.charAt(0).toUpperCase() + freq.slice(1)}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={attendanceData || []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <defs>
                        <linearGradient id="presentGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.9} />
                          <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.6} />
                        </linearGradient>
                        <linearGradient id="absentGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.9} />
                          <stop offset="95%" stopColor="#e11d48" stopOpacity={0.6} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="day" tick={{ fill: 'rgba(240,244,255,0.5)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                      <YAxis tick={{ fill: 'rgba(240,244,255,0.5)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                      <Tooltip contentStyle={customTooltipStyle} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
                      <Legend wrapperStyle={{ color: 'rgba(240,244,255,0.6)', fontSize: '12px' }} />
                      <Bar dataKey="presentStudentsCount" fill="url(#presentGradient)" radius={[4, 4, 0, 0]} name="Present" />
                      <Bar dataKey="absentStudentsCount" fill="url(#absentGradient)" radius={[4, 4, 0, 0]} name="Absent" />
                      {(!attendanceData || attendanceData.length === 0) && (
                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="rgba(240,244,255,0.25)" fontSize="22" fontWeight="600">
                          <tspan x="50%" dy="-0.5em">No Record</tspan>
                          <tspan x="50%" dy="1em">Found</tspan>
                        </text>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Dues Table + Pie Chart */}
              <div className="section-card">
                <div className="section-card-header">
                  <div className="section-title-wrap">
                    <div className="section-icon"><i className="bi bi-table"></i></div>
                    <h4 className="section-title">Current Session Unpaid Record</h4>
                  </div>
                </div>
                <div className="section-body">
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th style={{ width: '8%' }}>Receipt No.</th>
                          <th style={{ width: '10%' }}>Admission No.</th>
                          <th style={{ width: '12%' }}>Student Name</th>
                          <th style={{ width: '8%' }}>Class</th>
                          <th style={{ width: '7%' }}>Roll No.</th>
                          <th style={{ width: '10%' }}>Mobile No.</th>
                          <th style={{ width: '12%' }}>Email</th>
                          <th style={{ width: '10%' }}>Payment Month</th>
                          <th style={{ width: '9%' }}>Session</th>
                          <th style={{ width: '7%' }}>Total Fee</th>
                          <th style={{ width: '7%' }}>Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duesData.map((item, index) => (
                          <tr key={index}>
                            <td>{item.recieptNo}</td>
                            <td>{item.admissionNo}</td>
                            <td>{item.studentName}</td>
                            <td>{item.className}</td>
                            <td>{item.rollNo}</td>
                            <td>{item.mobileNo}</td>
                            <td>{item.email}</td>
                            <td>{item.paymentMonth}</td>
                            <td>{item.session}</td>
                            <td style={{ color: '#F4B942', fontFamily: 'JetBrains Mono, monospace' }}>₹{item.totalFee.toFixed(2)}</td>
                            <td style={{ color: '#10B981', fontFamily: 'JetBrains Mono, monospace' }}>₹{item.paidAmount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pie Chart */}
                  <div style={{ marginTop: '32px', textAlign: 'center' }}>
                    <div className="section-title-wrap" style={{ justifyContent: 'center', marginBottom: '20px' }}>
                      <div className="section-icon"><i className="bi bi-pie-chart-fill"></i></div>
                      <h4 className="section-title">Attendance — {attendancePieLabel}</h4>
                    </div>
                    <div className="pie-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px 0' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <defs>
                            <linearGradient id="presentPieGradient" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor="#38BDF8" />
                              <stop offset="100%" stopColor="#0ea5e9" />
                            </linearGradient>
                            <linearGradient id="absentPieGradient" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor="#F4B942" />
                              <stop offset="100%" stopColor="#f59e0b" />
                            </linearGradient>
                          </defs>
                          <Pie
                            data={todayAttendancePie}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            innerRadius={50}
                            paddingAngle={3}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                            labelLine={{ stroke: 'rgba(240,244,255,0.3)' }}
                          >
                            <Cell fill="url(#presentPieGradient)" stroke="rgba(56,189,248,0.3)" strokeWidth={1} />
                            <Cell fill="url(#absentPieGradient)" stroke="rgba(244,185,66,0.3)" strokeWidth={1} />
                          </Pie>
                          <Tooltip contentStyle={customTooltipStyle} />
                          <Legend wrapperStyle={{ color: 'rgba(240,244,255,0.6)', fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amount Collection Line Chart */}
              <div className="section-card">
                <div className="section-card-header">
                  <div className="section-title-wrap">
                    <div className="section-icon"><i className="bi bi-graph-up-arrow"></i></div>
                    <h4 className="section-title">Amount Collection</h4>
                  </div>
                  <div className="freq-controls">
                    <span className="freq-label-text">View:</span>
                    {[
                      { value: "monthly", label: "Monthly" },
                      { value: "weekly", label: "Weekly" },
                      { value: "daily", label: "Yearly" },
                    ].map((opt) => (
                      <div className="freq-btn" key={opt.value}>
                        <input
                          type="radio"
                          id={`collect-${opt.value}`}
                          name="collection-frequency"
                          value={opt.value}
                          checked={collectionViewFrequency === opt.value}
                          onChange={handleAmountFrequencyChange}
                        />
                        <label htmlFor={`collect-${opt.value}`}>{opt.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="section-body">
                  <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={collectionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <defs>
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#38BDF8" />
                          <stop offset="100%" stopColor="#8B5CF6" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="day" tick={{ fill: 'rgba(240,244,255,0.5)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                      <YAxis tick={{ fill: 'rgba(240,244,255,0.5)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                      <Tooltip contentStyle={customTooltipStyle} cursor={{ stroke: 'rgba(56,189,248,0.3)', strokeWidth: 1 }} />
                      <Legend wrapperStyle={{ color: 'rgba(240,244,255,0.6)', fontSize: '12px' }} />
                      <Line
                        type="monotone"
                        dataKey="collectedAmount"
                        stroke="url(#lineGradient)"
                        strokeWidth={2.5}
                        dot={{ fill: '#38BDF8', r: 4, strokeWidth: 0 }}
                        activeDot={{ r: 7, fill: '#38BDF8', stroke: 'rgba(56,189,248,0.4)', strokeWidth: 4 }}
                        name="Collected Amount"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>



       {/* Login Details Modal */}
{loginModalOpen && (
  <div style={{
    position: "fixed", inset: 0, zIndex: 1000,
    background: "rgba(11,31,64,0.55)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 24,
  }} onClick={() => setLoginModalOpen(false)}>
    <div style={{
      background: "#fff", borderRadius: 20, width: "100%", maxWidth: 800,
      maxHeight: "80vh", display: "flex", flexDirection: "column",
      boxShadow: "0 24px 60px rgba(11,31,64,0.2)",
      border: "1.5px solid var(--glass-border)",
    }} onClick={e => e.stopPropagation()}>

      {/* Modal Header */}
      <div style={{
        padding: "20px 28px", borderBottom: "1.5px solid var(--glass-border)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: "linear-gradient(90deg, rgba(56,189,248,0.05), transparent)",
        borderRadius: "20px 20px 0 0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="section-icon"><i className="bi bi-key-fill"></i></div>
          <div>
            <h4 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "var(--text-primary)" }}>
              System Login Details
            </h4>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Total: {dashData.totalLogin} logins
            </span>
          </div>
        </div>
        <button onClick={() => setLoginModalOpen(false)} style={{
          background: "rgba(244,63,94,0.1)", border: "none", borderRadius: 8,
          width: 34, height: 34, cursor: "pointer", fontSize: 16, color: "#e11d48",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>✕</button>
      </div>

      {/* Modal Body */}
      <div style={{ overflowY: "auto", padding: "20px 28px", flex: 1 }}>
        {loginModalLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <div className="custom-spinner"></div>
          </div>
        ) : loginModalData.length === 0 ? (
          <div className="bws-empty">
            <span className="bws-empty-icon">🔑</span>
            <span className="bws-empty-text">No login records found</span>
          </div>
        ) : (
          <div className="bws-table-wrap" style={{ maxHeight: "none" }}>
            <table className="bws-table">
             <thead>
  <tr>
    <th>#</th>
    <th>User Name</th>
    <th>Login Time</th>
    <th>IP Address</th>
    <th>Branch</th>
  </tr>
</thead>

              <tbody>
  {loginModalData.map((item, idx) => (
    <tr key={idx}>
      <td>{idx + 1}</td>
      <td><strong style={{ color: "#0B1F40" }}>{item.userName || "—"}</strong></td>
      <td className="bws-center" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>
        {item.logDate ? new Date(item.logDate).toLocaleString() : "—"}
      </td>
      <td>{item.ipAddress || "—"}</td>
      <td>{item.branchName || "—"}</td>
    </tr>
  ))}
</tbody>

            </table>
          </div>
        )}
      </div>

      {/* Modal Footer */}
      <div style={{
        padding: "14px 28px", borderTop: "1.5px solid var(--glass-border)",
        display: "flex", justifyContent: "flex-end", gap: 10,
        borderRadius: "0 0 20px 20px",
      }}>
        <button onClick={() => setLoginModalOpen(false)} style={{
          padding: "9px 24px", borderRadius: 10, border: "1.5px solid var(--glass-border)",
          background: "#fff", color: "var(--text-secondary)", fontSize: 13, fontWeight: 600,
          cursor: "pointer", fontFamily: "Sora, sans-serif",
        }}>Close</button>
      </div>
    </div>
  </div>
)}
    </>
  );
};

export default Dashboard;